import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { buildIntegrationPrompt } from '@admin-platform/shared/integration-content';

const PROVISION = `// 1. Discover an active region with GET https://api.revvly.ai/api/v1/regions.
// 2. Create the billing client through POST https://api.revvly.ai/api/v1/graphql.
mutation CreateClient($input: CreateClientInput!) {
  createClient(input: $input) {
    id
    externalId
    name
    region { id name defaultCurrency }
  }
}

{
  "input": {
    "name": "Acme Corporation",
    "regionId": "<active-region-id>",
    "externalId": "acct_123",
    "paymentTermsDays": 30
  }
}`;

const PROVISION_SUBSCRIPTION = `// 3. Provision a subscription for that client, with its own externalId.
mutation CreateSubscription($input: CreateSubscriptionInput!) {
  createSubscription(input: $input) {
    id
    externalId
    status
    period
    currency
  }
}

{
  "input": {
    "clientExternalId": "acct_123",
    "externalId": "sub-acme-growth-2026",
    "bundleId": "<bundle-id>",
    "period": "MONTHLY",
    "startDate": "2026-08-01T00:00:00.000Z",
    "components": [
      { "componentId": "<component-id>", "startingQuantity": 2 }
    ]
  }
}`;

const BILLING_READ = `// POST https://api.revvly.ai/api/v1/graphql
query ClientBillingStatus($externalId: String!) {
  client(externalId: $externalId) {
    id
    name
    invoices(status: "OVERDUE", limit: 10) {
      invoiceNumber
      dueDate
      amount { amount currency }
      paymentStatus
      pdfUrl
    }
    paymentSummary { currency outstanding overdue overdueCount }
  }
}

{ "externalId": "acct_123" }`;

const AGENT_PROMPT = buildIntegrationPrompt('clientApi');

export function PublicGraphQLIntegrationGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Integrate with public GraphQL — Revvly Docs';
  }, []);
  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Public GraphQL integration</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Provision and read clients through <em>GraphQL</em>.
        </h1>
        <p className="dp-oneliner">
          Give a backend one intentional contract for billing client creation and receivables reads.
        </p>
      </header>
      <section className="dp-section">
        <h2>Give this prompt to an integration agent</h2>
        <pre className="api-code">{AGENT_PROMPT}</pre>
      </section>
      <section className="dp-section">
        <h2>Example 1: provision a billing client</h2>
        <p className="dp-plain">
          A client is billing-ready, so a valid active region is required. This is separate from the
          CRM prospect flow, which does not require a region.
        </p>
        <pre className="api-code">{PROVISION}</pre>
      </section>
      <section className="dp-section">
        <h2>Example 2: provision a subscription for that client</h2>
        <p className="dp-plain">
          Set the subscription&rsquo;s own <span className="api-inline">externalId</span> at
          creation &mdash; separate from the client&rsquo;s &mdash; so the usage-ingestion API can
          find it by <span className="api-inline">externalSubscriptionId</span>.{' '}
          <span className="api-inline">components[].startingQuantity</span> seeds an initial count
          (e.g. starting seats) so the first invoice bills correctly.
        </p>
        <pre className="api-code">{PROVISION_SUBSCRIPTION}</pre>
      </section>
      <section className="dp-section">
        <h2>Example 3: show a customer&rsquo;s outstanding balance</h2>
        <p className="dp-plain">
          Query by your own external id and ask only for overdue invoices and per-currency payment
          totals. Request the PDF link only when rendering a download action.
        </p>
        <pre className="api-code">{BILLING_READ}</pre>
      </section>
      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link
              to="/docs/api/graphql-public"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Public GraphQL API reference ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/guides/prospect-form-integration"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Prospect form integration ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
